/**
 * audioConverter.ts
 * 使用 ffmpeg.wasm 将音频文件转换为 MP3 格式
 * 仅在需要时加载 FFmpeg（懒加载），转换完成后立即释放
 */

import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';
import { FFmpegCDNLoader } from './ffmpegCDNLoader';

/** 需要转换的格式（这些格式文件体积大或兼容性差） */
const NEEDS_CONVERT_EXTS = new Set(['.wav', '.flac', '.aiff', '.aif']);

export interface ConvertProgress {
  stage: 'loading' | 'converting';
  message: string;
}

/**
 * 如果文件是 wav/flac 等大体积格式，转换为 mp3；否则原样返回
 * @param file 原始音频文件
 * @param onProgress 进度回调（可选）
 * @returns 转换后的 File 对象（mp3），或原始文件
 */
export async function convertAudioToMp3(
  file: File,
  onProgress?: (p: ConvertProgress) => void,
): Promise<File> {
  const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

  // 不需要转换，直接返回
  if (!NEEDS_CONVERT_EXTS.has(ext)) return file;

  onProgress?.({ stage: 'loading', message: '正在加载转换工具...' });

  let ffmpeg: FFmpeg | null = null;
  try {
    const loader = new FFmpegCDNLoader();
    const result = await loader.loadFFmpeg();
    ffmpeg = result.ffmpeg;

    onProgress?.({ stage: 'converting', message: '正在转换音频格式...' });

    const inputName = `input${ext}`;
    const outputName = 'output.mp3';

    // 写入输入文件
    await ffmpeg.writeFile(inputName, await fetchFile(file));

    // 转换：128kbps mp3，保留原始采样率
    await ffmpeg.exec([
      '-i', inputName,
      '-codec:a', 'libmp3lame',
      '-qscale:a', '2',   // VBR ~190kbps，质量好体积小
      '-ar', '44100',
      outputName,
    ]);

    // 读取输出（readFile 返回 Uint8Array | string）
    const data = await ffmpeg.readFile(outputName);
    const uint8 = typeof data === 'string'
      ? new TextEncoder().encode(data)
      : data;
    const mp3Buffer = uint8.buffer.slice(uint8.byteOffset, uint8.byteOffset + uint8.byteLength) as ArrayBuffer;

    // 构造新文件名（替换扩展名）
    const baseName = file.name.substring(0, file.name.lastIndexOf('.'));
    const mp3File = new File([mp3Buffer], `${baseName}.mp3`, { type: 'audio/mpeg' });

    return mp3File;
  } finally {
    // 转换完成后释放 FFmpeg 实例，避免内存占用
    if (ffmpeg) {
      try { await ffmpeg.terminate(); } catch { /* ignore */ }
    }
  }
}
